/**
 * React hooks for Web Push notification management.
 * Handles subscription, unsubscription, and status checking.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '@/services/api';
import {
  isPushSupported,
  requestPermission,
  subscribeToPush,
  getPermissionState,
} from '@/lib/serviceWorker';
import { QUERY_RETRY_CONFIG } from '../types/statcast';

const pushKeys = {
  status: ['push-status'] as const,
  vapidKey: ['vapid-key'] as const,
};

/** Check if push is supported and get current status. */
export function usePushStatus() {
  const { getToken, isSignedIn } = useAuth();
  return useQuery({
    queryKey: pushKeys.status,
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const status = await mlbApi.getPushStatus(token);
      return {
        ...status,
        browserSupported: isPushSupported(),
        permissionState: getPermissionState(),
      };
    },
    enabled: !!isSignedIn,
    staleTime: 60_000,
    ...QUERY_RETRY_CONFIG,
  });
}

/** Subscribe to push notifications (request permission + register). */
export function useSubscribePush() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const permission = await requestPermission();
      if (permission !== 'granted') {
        throw new Error('Permission denied');
      }

      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { public_key } = await mlbApi.getVapidKey(token);

      const subscription = await subscribeToPush(public_key);
      if (!subscription) throw new Error('Subscription failed');

      await mlbApi.subscribePush(token, subscription, navigator.userAgent?.slice(0, 300));
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushKeys.status });
    },
  });
}

/** Unsubscribe from push notifications. */
export function useUnsubscribePush() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await mlbApi.unsubscribePush(token, sub.endpoint);
        await sub.unsubscribe();
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pushKeys.status });
    },
  });
}
